"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import type { DemoStep } from "../types";
import { DemoCallout } from "./demo-callout";
import { DemoHotspotLayer, type DemoOpener } from "./demo-hotspot-layer";
import { useDemoFrame } from "./demo-viewport-context";

interface CameraState {
  scale: number;
  x: number;
  y: number;
}

const ZOOM_SCALE = 1.38;
const ZOOM_SCALE_FLOOR = 1.08;
const SCROLL_MS = 520;
const CAMERA_MS = 680;
const DEFAULT_CAMERA: CameraState = { scale: 1, x: 0, y: 0 };

interface CalloutPos {
  left: number;
  top: number;
  placement: "above" | "below";
}

const CALLOUT_EST_HEIGHT = 100;
const CALLOUT_GAP = 14;

interface DemoStageProps {
  children: React.ReactNode;
  currentStep: DemoStep;
  activeCalloutText?: string;
  /**
   * `"off"` mantém a rolagem até o alvo e desliga a aproximação da câmera. A
   * página usa isto: lá a pessoa navega clicando, e aproximar entre cliques
   * dava a sensação de assistir a um vídeo em vez de usar o app.
   */
  cameraMode?: "zoom" | "off";
  /** Presente = a tela é navegável por clique, e a camada de pontos monta. */
  onOpen?: (opener: DemoOpener) => void;
}

export function DemoStage({
  children,
  currentStep,
  activeCalloutText = "",
  cameraMode = "zoom",
  onOpen,
}: DemoStageProps) {
  const { mode } = useDemoFrame();
  const containerRef = useRef<HTMLDivElement>(null);
  const calloutTargetRef = useRef<HTMLElement | null>(null);
  const runIdRef = useRef(0);
  const [camera, setCamera] = useState<CameraState>(DEFAULT_CAMERA);
  const [calloutPos, setCalloutPos] = useState<CalloutPos | null>(null);
  const [calloutVisible, setCalloutVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updateCalloutPosition = useCallback((target: HTMLElement) => {
    const container = containerRef.current;
    const screenEl =
      container?.closest<HTMLElement>("[data-demo-screen]") ?? container;
    if (!screenEl) return;

    const bounds = screenEl.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    const anchorX = rect.left + rect.width / 2;
    const anchorY = rect.top + rect.height / 2;

    if (
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      anchorX < bounds.left ||
      anchorX > bounds.right ||
      anchorY < bounds.top ||
      anchorY > bounds.bottom
    ) {
      setCalloutPos(null);
      return;
    }

    const edgePadding = 16;
    const left = Math.min(
      bounds.right - edgePadding,
      Math.max(bounds.left + edgePadding, anchorX),
    );

    /*
     * `placement` governa três coisas ao mesmo tempo: de que lado do alvo o
     * balão fica, para onde a âncora o desloca (`translate(-50%,-100%)` acima,
     * `-50%,0` abaixo) e para que lado aponta o rabicho. Por isso a escolha do
     * lado e o cálculo do `top` moram juntos: quando o lado mudava sem o
     * `placement` mudar junto, o balão trocava de lugar e o rabicho continuava
     * apontando para o lado velho — era o caso do alvo alto (a grade de
     * creators), que não cabe acima nem abaixo.
     */
    const needed = CALLOUT_EST_HEIGHT + CALLOUT_GAP;
    const spaceAbove = rect.top - bounds.top;
    const spaceBelow = bounds.bottom - rect.bottom;

    const placement: "above" | "below" =
      spaceAbove >= needed
        ? "above"
        : spaceBelow >= needed
          ? "below"
          : // Alvo mais alto que a tela: fica do lado mais folgado, ainda
            // apontando para ele.
            spaceAbove >= spaceBelow
            ? "above"
            : "below";

    const top =
      placement === "above" ? rect.top - CALLOUT_GAP : rect.bottom + CALLOUT_GAP;

    setCalloutPos({ left, top, placement });
  }, []);

  const syncCalloutPosition = useCallback(() => {
    const target = calloutTargetRef.current;
    if (target) {
      updateCalloutPosition(target);
    }
  }, [updateCalloutPosition]);

  const animateToOverview = useCallback(async (runId: number) => {
    setCalloutVisible(false);
    setCalloutPos(null);
    calloutTargetRef.current = null;
    setCamera(DEFAULT_CAMERA);

    const scrollParent = getDemoScrollParent(containerRef.current);
    if (scrollParent) {
      resetModalScrollContainers(scrollParent);
      await animateScrollTo(scrollParent, 0, SCROLL_MS);
    }

    if (runId !== runIdRef.current) return;
    await waitMs(CAMERA_MS);
  }, []);

  const computeZoom = useCallback(async () => {
    const runId = ++runIdRef.current;
    const targetId = currentStep.zoomTargetId;

    if (!targetId || !containerRef.current) {
      await animateToOverview(runId);
      return;
    }

    await waitForLayout();

    const screenEl =
      containerRef.current.closest<HTMLElement>("[data-demo-screen]") ??
      containerRef.current;
    const target = screenEl.querySelector<HTMLElement>(
      `[data-demo-id="${targetId}"]`,
    );
    if (!target) {
      await animateToOverview(runId);
      return;
    }

    setCalloutVisible(false);
    setCalloutPos(null);
    calloutTargetRef.current = null;

    const scrollParent = getDemoScrollParent(containerRef.current);
    if (scrollParent) {
      await animateScrollChain(target, scrollParent, SCROLL_MS, () => runId !== runIdRef.current);
    }

    if (runId !== runIdRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    if (mode === "desktop" && cameraMode === "zoom" && !currentStep.disableZoom) {
      const preferredScale = currentStep.zoomScale ?? ZOOM_SCALE;
      const nextCamera = computeCamera(
        target,
        container,
        resolveZoomScale(target, container, preferredScale),
      );
      setCamera(nextCamera);
    } else {
      setCamera(DEFAULT_CAMERA);
    }

    await waitMs(CAMERA_MS);
    if (runId !== runIdRef.current) return;

    calloutTargetRef.current = target;
    updateCalloutPosition(target);
    setCalloutVisible(true);
  }, [currentStep.zoomTargetId, currentStep.zoomScale, currentStep.disableZoom, cameraMode, mode, animateToOverview, updateCalloutPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      void computeZoom();
    }, 60);
    return () => window.clearTimeout(id);
  }, [computeZoom]);

  useEffect(() => {
    if (!calloutVisible || !calloutTargetRef.current) return;

    let rafId = 0;
    const scheduleSync = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(syncCalloutPosition);
    };

    scheduleSync();

    window.addEventListener("scroll", scheduleSync, { passive: true, capture: true });
    window.addEventListener("resize", scheduleSync);

    const container = containerRef.current;
    const scrollRoot = getDemoScrollParent(container);
    scrollRoot?.addEventListener("scroll", scheduleSync, { passive: true });

    const modalScrolls = scrollRoot
      ? Array.from(scrollRoot.querySelectorAll<HTMLElement>("[data-demo-modal-scroll]"))
      : [];
    modalScrolls.forEach((element) => {
      element.addEventListener("scroll", scheduleSync, { passive: true });
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", scheduleSync, true);
      window.removeEventListener("resize", scheduleSync);
      scrollRoot?.removeEventListener("scroll", scheduleSync);
      modalScrolls.forEach((element) => {
        element.removeEventListener("scroll", scheduleSync);
      });
    };
  }, [calloutVisible, syncCalloutPosition, camera, currentStep.id]);

  const showCallout =
    !!currentStep.callout && calloutVisible && !!activeCalloutText && calloutPos !== null;

  const calloutLayer =
    mounted && showCallout && calloutPos
      ? createPortal(
          <div
            className="fixed z-[99999] pointer-events-none"
            style={{
              left: calloutPos.left,
              top: calloutPos.top,
              transform:
                calloutPos.placement === "above"
                  ? "translate(-50%, -100%)"
                  : "translate(-50%, 0)",
            }}
          >
            <DemoCallout
              text={activeCalloutText}
              visible={calloutVisible}
              placement={calloutPos.placement}
            />
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div className="relative isolate z-0 h-full min-h-0 w-full">
        <div ref={containerRef} className="relative h-full min-h-0 w-full overflow-hidden bg-background">
          <motion.div
            className="relative z-0 h-full min-h-0 w-full"
            animate={
              mode === "desktop"
                ? { scale: camera.scale, x: camera.x, y: camera.y }
                : { scale: DEFAULT_CAMERA.scale, x: DEFAULT_CAMERA.x, y: DEFAULT_CAMERA.y }
            }
            transition={{ duration: CAMERA_MS / 1000, ease: [0.4, 0, 0.2, 1] }}
            style={{
              transformOrigin: mode === "desktop" ? "center center" : "top center",
            }}
          >
            <div className="h-full min-h-0 w-full">{children}</div>
          </motion.div>
        </div>
      </div>
      {calloutLayer}
      {onOpen && (
        <DemoHotspotLayer containerRef={containerRef} visible onSelect={onOpen} />
      )}
    </>
  );
}

function getDemoScrollParent(container: HTMLElement | null): HTMLElement | null {
  if (!container) return null;
  return container.querySelector<HTMLElement>("[data-demo-scroll-root]");
}

function isScrollableElement(element: HTMLElement): boolean {
  const { overflowY } = window.getComputedStyle(element);
  if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") {
    return false;
  }
  return element.scrollHeight > element.clientHeight + 1;
}

/** Scroll containers do alvo até a raiz da demo (inner → outer). */
function getScrollChain(target: HTMLElement, rootScrollParent: HTMLElement): HTMLElement[] {
  const chain: HTMLElement[] = [];
  let node: HTMLElement | null = target.parentElement;

  while (node && node !== rootScrollParent) {
    if (isScrollableElement(node)) {
      chain.push(node);
    }
    node = node.parentElement;
  }

  chain.push(rootScrollParent);
  return chain;
}

function resetModalScrollContainers(rootScrollParent: HTMLElement): void {
  rootScrollParent.querySelectorAll<HTMLElement>("[data-demo-modal-scroll]").forEach((element) => {
    element.scrollTop = 0;
  });
}

async function animateScrollChain(
  target: HTMLElement,
  rootScrollParent: HTMLElement,
  durationMs: number,
  isCancelled: () => boolean,
): Promise<void> {
  const chain = getScrollChain(target, rootScrollParent);

  for (const scrollElement of chain) {
    if (isCancelled()) return;
    const nextScrollTop = computeTargetScrollTop(target, scrollElement);
    await animateScrollTo(scrollElement, nextScrollTop, durationMs);
  }
}

function waitForLayout(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

function getAncestorScale(el: HTMLElement): number {
  let scale = 1;
  let node: HTMLElement | null = el.parentElement;

  while (node) {
    const transform = window.getComputedStyle(node).transform;
    if (transform && transform !== "none") {
      const matrix = new DOMMatrixReadOnly(transform);
      scale *= matrix.a;
    }
    node = node.parentElement;
  }

  return scale || 1;
}

function computeTargetScrollTop(target: HTMLElement, scrollParent: HTMLElement): number {
  const scale = getAncestorScale(scrollParent);
  const targetRect = target.getBoundingClientRect();
  const parentRect = scrollParent.getBoundingClientRect();
  const visualDelta = targetRect.top - parentRect.top;
  const layoutDelta = visualDelta / scale;
  const maxScroll = Math.max(0, scrollParent.scrollHeight - scrollParent.clientHeight);

  const nextScrollTop =
    scrollParent.scrollTop +
    layoutDelta -
    scrollParent.clientHeight / 2 +
    target.offsetHeight / 2;

  return Math.max(0, Math.min(nextScrollTop, maxScroll));
}

function resolveZoomScale(
  target: HTMLElement,
  container: HTMLElement,
  preferredScale: number,
): number {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const coverage = Math.max(
    targetRect.width / containerRect.width,
    targetRect.height / containerRect.height,
  );

  let scale = preferredScale;

  if (coverage >= 0.72) {
    scale = Math.min(scale, 1.12);
  } else if (coverage >= 0.52) {
    scale = Math.min(scale, 1 + (preferredScale - 1) * 0.5);
  } else if (coverage >= 0.38) {
    scale = Math.min(scale, 1 + (preferredScale - 1) * 0.75);
  }

  return Math.max(ZOOM_SCALE_FLOOR, scale);
}

function computeCamera(
  target: HTMLElement,
  container: HTMLElement,
  scale: number,
): CameraState {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const targetCx = targetRect.left - containerRect.left + targetRect.width / 2;
  const targetCy = targetRect.top - containerRect.top + targetRect.height / 2;
  const containerCx = containerRect.width / 2;
  const containerCy = containerRect.height / 2;

  return {
    scale,
    x: (containerCx - targetCx) * (scale - 1),
    y: (containerCy - targetCy) * (scale - 1),
  };
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function animateScrollTo(
  element: HTMLElement,
  targetScrollTop: number,
  durationMs: number,
): Promise<void> {
  return new Promise((resolve) => {
    const startScrollTop = element.scrollTop;
    const delta = targetScrollTop - startScrollTop;

    if (Math.abs(delta) < 1) {
      resolve();
      return;
    }

    const startTime = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / durationMs, 1);
      element.scrollTop = startScrollTop + delta * easeInOutCubic(progress);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(tick);
  });
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
